import { useCallback, useEffect, useMemo, useState } from "react";
import { k, onExternalChange, readJSON, writeJSON } from "@/lib/storage";
import type { Mastery } from "@/types/lex";

const KEY = k("mastery.v1");

/**
 * Stored shape: `{ "1:3": "known", "1:5": "learning", ... }`
 * Keys are `${day}:${wordIndex}`. Words without an entry are "untouched".
 */
export type MasteryMap = Record<string, Exclude<Mastery, "untouched">>;

function entryKey(day: number, wordIndex: number): string {
  return `${day}:${wordIndex}`;
}

/**
 * Module-level subscription bus so multiple components stay in sync without
 * needing a Context provider. Useful because Word, Day, Home, Progress all
 * read mastery state independently and any update should propagate.
 */
const listeners = new Set<() => void>();
let snapshot: MasteryMap = readJSON<MasteryMap>(KEY, {});

function emit() {
  for (const fn of listeners) fn();
}

// Mirror another tab's ledger. The schedule it derives from has its own
// listener, so the two arrive independently and both tabs converge.
onExternalChange(KEY, () => {
  snapshot = readJSON<MasteryMap>(KEY, {});
  emit();
});

function set(day: number, wordIndex: number, value: Mastery): void {
  const key = entryKey(day, wordIndex);
  const next: MasteryMap = { ...snapshot };
  if (value === "untouched") {
    delete next[key];
  } else {
    next[key] = value;
  }
  snapshot = next;
  writeJSON(KEY, next);
  emit();
}

function get(day: number, wordIndex: number): Mastery {
  return snapshot[entryKey(day, wordIndex)] ?? "untouched";
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Read + write the mastery for a single word. Triggers a re-render whenever
 * any word's mastery changes anywhere in the app.
 */
export function useMastery(day: number, wordIndex: number): [Mastery, (v: Mastery) => void] {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((n) => n + 1)), []);
  const value = get(day, wordIndex);
  const setValue = useCallback(
    (v: Mastery) => set(day, wordIndex, v),
    [day, wordIndex]
  );
  return [value, setValue];
}

/**
 * Read mastery for an entire day at once — used by DayDetail and Home to
 * compute aggregate counts.
 */
export function useDayMastery(day: number): {
  byIndex: (i: number) => Mastery;
  counts: { known: number; learning: number; unknown: number; touched: number };
} {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((n) => n + 1)), []);

  return useMemo(() => {
    const prefix = `${day}:`;
    const counts = { known: 0, learning: 0, unknown: 0, touched: 0 };
    for (const [k2, v] of Object.entries(snapshot)) {
      if (!k2.startsWith(prefix)) continue;
      counts.touched += 1;
      counts[v] += 1;
    }
    return { byIndex: (i: number) => get(day, i), counts };
  }, [day, snapshot]);
}

/**
 * Global mastery view — used by the Progress screen and Home stats strip.
 */
export function useAllMastery(): {
  countByDay: (day: number) => { known: number; learning: number; unknown: number; touched: number };
  totals: { known: number; learning: number; unknown: number; touched: number };
  forEach: (fn: (day: number, index: number, value: Mastery) => void) => void;
} {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((n) => n + 1)), []);

  return useMemo(() => {
    const perDay = new Map<number, { known: number; learning: number; unknown: number; touched: number }>();
    const totals = { known: 0, learning: 0, unknown: 0, touched: 0 };
    for (const [key, v] of Object.entries(snapshot)) {
      const [dayStr] = key.split(":");
      const day = Number(dayStr);
      if (!Number.isFinite(day)) continue;
      const cell = perDay.get(day) ?? { known: 0, learning: 0, unknown: 0, touched: 0 };
      cell.touched += 1;
      cell[v] += 1;
      perDay.set(day, cell);
      totals.touched += 1;
      totals[v] += 1;
    }
    return {
      countByDay: (day: number) =>
        perDay.get(day) ?? { known: 0, learning: 0, unknown: 0, touched: 0 },
      totals,
      forEach: (fn) => {
        for (const [key, v] of Object.entries(snapshot)) {
          const [d, i] = key.split(":");
          fn(Number(d), Number(i), v);
        }
      },
    };
  }, [snapshot]);
}

/**
 * Imperative setter for use outside of a React render — e.g., from inside an
 * event handler that iterates over many words. Notifies any subscribed
 * hooks the same way `useMastery`'s setter does.
 */
export function recordMastery(day: number, wordIndex: number, value: Mastery): void {
  set(day, wordIndex, value);
}

/**
 * Every stored label, for callers outside React — the SRS seeder and the
 * Settings export both need the raw map.
 */
export function allMastery(): Readonly<MasteryMap> {
  return snapshot;
}

export function replaceMastery(next: MasteryMap): void {
  snapshot = next;
  writeJSON(KEY, next);
  emit();
}

/**
 * Wipe all mastery data. Wired to Settings → "Reset progress".
 */
export function resetMastery(): void {
  snapshot = {};
  writeJSON(KEY, snapshot);
  emit();
}
